import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AttachmentDirectDownloadDetailDto, DocumentDownloadDisclosureAuditDto } from 'webcommon/shared';
import { DisclosureAuditRepository } from 'webcommon/webapi';
import { ApNotificationService } from './ApNotificationService';

@Injectable({ providedIn: 'root' })
export class AttachmentDirectDownloadService {

  // Handles direct-to-disk attachment downloads using the File System Access API (showSaveFilePicker).
  // Content comes form a pre-fetched download URL, not processed in the client.
  //
  // showSaveFilePicker() opens a native "Save file" dialog (Chrome/Edge only, HTTPS + user gesture required).
  // It returns a FileSystemFileHandle used to write the file via createWritable().
  // Throws AbortError if the user cancels, SecurityError if called outside a user-activation window.
  //
  // IMPORTANT: Chrome/Edge create the destination file on disk the moment the user confirms the save
  // dialog — before any content is written. Callers must validate that FullFileName and DownloadUrl are
  // present before calling getSaveFileHandle(), so the picker is never shown when there is nothing to download.
  //
  // Firefox and Safari do not support showSaveFilePicker; this service falls back to an <a download>
  // navigation for those browsers.

  constructor(
    private readonly apNotificationService: ApNotificationService,
    private readonly disclosureAuditRepository: DisclosureAuditRepository,
  ) { }

  downloadAttachment(
    downloadInfo: AttachmentDirectDownloadDetailDto,
    handlePromise: Promise<FileSystemFileHandle> | null,
  ): Observable<void> {
    return this.resolveHandle$(handlePromise).pipe(
      switchMap((handle) => {
        return handle === 'cancelled' ? of(undefined) : this.saveContent$(downloadInfo, handle);
      }),
      catchError((_error: unknown) => {
        this.apNotificationService.error(
          'The file could not be downloaded. Please try again or contact support.',
          'Download Failed',
        );
        return of(undefined); // Swallow error so finalize() still fires in the component
      }),
    );
  }

  // Resolves the file handle promise, treating AbortError (user cancelled) as a sentinel
  // and any other error as null (fall through to <a download>).
  private resolveHandle$(handlePromise: Promise<FileSystemFileHandle> | null) {
    if (!handlePromise) { return of(null); } // showSaveFilePicker not available (Firefox/Safari)
    return from(handlePromise).pipe(
      catchError((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return of('cancelled' as const); // user dismissed the picker — skip download
        }
        return of(null); // unexpected error — fall through to <a download>
      }),
    );
  }

  // Dispatches to the appropriate download strategy based on handle availability and SkipFilePicker flag, then audits.
  // If SkipFilePicker is true, bypasses the file picker and uses direct <a> tag download.
  // Throws if DownloadUrl is missing, the HTTP response is not ok, or the body is empty.
  private saveContent$(downloadInfo: AttachmentDirectDownloadDetailDto, handle: FileSystemFileHandle | null): Observable<void> {
    if (!downloadInfo.DownloadUrl) { throw new Error('No download URL available for this attachment.'); }

    if (!downloadInfo.AttachmentDetails.SkipFilePicker && handle) {
      return this.fetchAndWriteToHandle$(downloadInfo.DownloadUrl, downloadInfo.AttachmentDetails.MimeType, handle)
        .pipe(switchMap(() => this.auditDocumentDownload(downloadInfo)));
    }

    // Firefox/Safari or SkipFilePicker: navigate the <a> tag directly to the pre-signed URL
    const a = document.createElement('a');
    a.href = downloadInfo.DownloadUrl;
    a.download = downloadInfo.AttachmentDetails.FullFileName;
    a.click();
    return this.auditDocumentDownload(downloadInfo);
  }

  // Fetches content from url and writes it to handle only after a successful, non-empty response —
  // ensuring createWritable() is never called if the download fails, which would leave an empty file.
  private fetchAndWriteToHandle$(url: string, mimeType: string, handle: FileSystemFileHandle): Observable<void> {
    return from(
      fetch(url)
        .then((r) => {
          if (!r.ok) {
            throw new Error(`Download failed: ${r.status} ${r.statusText}`);
          }
          return r.arrayBuffer();
        })
        .then(async (buf) => {
          if (buf.byteLength === 0) {
            throw new Error('Download failed: response body was empty.');
          }
          const blob = new Blob([buf], { type: mimeType });
          const writable = await (handle as any).createWritable();
          await writable.write(blob);
          await writable.close();
        })
    );
  }

  // Generates a Record Disclosure Audit for the document download.
  // PatientId, AttachmentId, and FileName are passed to the server; the current user is resolved seer-side.
  private auditDocumentDownload(downloadInfo: AttachmentDirectDownloadDetailDto): Observable<void> {
    if (downloadInfo.AttachmentDetails.PatientId == null) { return of(undefined); }
    const downloadAuditDto: DocumentDownloadDisclosureAuditDto = {
      PatientId: downloadInfo.AttachmentDetails.PatientId,
      DocumentId: downloadInfo.AttachmentDetails.AttachmentId,
      FileName: downloadInfo.AttachmentDetails.FullFileName,
      Description: 'Document Downloaded: ' + downloadInfo.AttachmentDetails.FullFileName,
    };
    return this.disclosureAuditRepository.auditDocumentDownload(downloadAuditDto).pipe(map(() => undefined));
  }
}
